import { Link } from 'react-router-dom';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  coverImage: string;
  categoryName: string;
  categorySlug: string;
  author: string;
  publishDate: string;
  readTime: number;
  views: number;
  isFeatured?: boolean;
  variant?: 'default' | 'large' | 'compact';
}

function formatViews(views: number): string {
  if (views >= 10000) {
    return (views / 10000).toFixed(1) + '万';
  }
  return views.toString();
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  coverImage,
  categoryName,
  categorySlug,
  author,
  publishDate,
  readTime,
  views,
  isFeatured,
  variant = 'default',
}: ArticleCardProps) {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (variant === 'large') {
    return (
      <article className="group relative overflow-hidden rounded-lg">
        <Link to={`/article/${slug}`} onClick={scrollToTop} className="block">
          <div className="relative w-full h-[420px] md:h-[520px] overflow-hidden rounded-lg">
            <img
              src={coverImage}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <span className="inline-block px-2.5 py-1 text-xs font-medium bg-accent-500 text-background-50 rounded-md mb-3">
                {categoryName}
              </span>
              {isFeatured && (
                <span className="inline-block px-2.5 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-md mb-3 ml-2">
                  独家
                </span>
              )}
              <h2 className="text-xl md:text-2xl font-heading font-bold text-white mb-2 leading-tight group-hover:text-primary-200 transition-colors">
                {title}
              </h2>
              <p className="text-sm text-white/70 line-clamp-2 mb-3">{excerpt}</p>
              <div className="flex items-center gap-4 text-xs text-white/50">
                <span>{author}</span>
                <span>{publishDate}</span>
                <span>{readTime} 分钟阅读</span>
                <span><i className="ri-eye-line mr-1"></i>{formatViews(views)}</span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group flex gap-4">
        <Link to={`/article/${slug}`} onClick={scrollToTop} className="shrink-0">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-lg overflow-hidden">
            <img
              src={coverImage}
              alt={title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/category/${categorySlug}`} onClick={scrollToTop}>
            <span className="text-xs text-primary-500 font-medium hover:text-primary-600 transition-colors">
              {categoryName}
            </span>
          </Link>
          <Link to={`/article/${slug}`} onClick={scrollToTop}>
            <h3 className="text-sm font-semibold text-foreground-900 mt-1 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
              {title}
            </h3>
          </Link>
          <div className="flex items-center gap-3 text-xs text-foreground-400 mt-1.5">
            <span>{publishDate}</span>
            <span>{formatViews(views)} 阅读</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all duration-300">
      <Link to={`/article/${slug}`} onClick={scrollToTop}>
        <div className="relative w-full h-48 overflow-hidden">
          <img
            src={coverImage}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute top-3 left-3">
            <span className="px-2 py-0.5 text-xs font-medium bg-accent-500/90 text-background-50 rounded-md">
              {categoryName}
            </span>
          </div>
        </div>
      </Link>
      <div className="p-4 md:p-5">
        <Link to={`/article/${slug}`} onClick={scrollToTop}>
          <h3 className="text-base font-heading font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors mb-2">
            {title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">{excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <div className="flex items-center gap-2">
            <span>{author}</span>
            <span>·</span>
            <span>{publishDate}</span>
          </div>
          <span>{readTime} 分钟</span>
        </div>
      </div>
    </article>
  );
}